import { Component, computed, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonDirective } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Store } from '@ngrx/store';
import { PermissionService, User } from '@workspace-src/app/shared';
import { selectUser } from '@workspace-app/state/global';
import { BillDataService, BillingChecklist } from '../bill-data.service';

@Component({
  selector: 'app-billing-checklist',
  standalone: true,
  imports: [FormsModule, CheckboxModule, ButtonDirective, InputTextModule, TooltipModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (!checklist() && canEdit()) {
      <div class="flex align-items-center gap-2 mt-2">
        <button
          pButton
          type="button"
          class="p-button-text p-button-sm"
          (click)="createChecklist()"><i class="pi pi-plus" pButtonIcon></i><span pButtonLabel>Add Checklist</span></button>
      </div>
    }
    @if (checklist()) {
      <div class="surface-ground border-round p-3 mt-2">
        <div class="flex justify-content-between align-items-center mb-2">
          <span class="font-semibold text-sm">Checklist</span>
          @if (checklist()!.items.length > 0) {
            <span class="text-xs text-500">{{ completedCount() }}/{{ checklist()!.items.length }}</span>
          }
        </div>
        @for (item of checklist()!.items; track item.key) {
          <div class="flex align-items-center gap-2 py-1">
            <p-checkbox
              [binary]="true"
              [(ngModel)]="item.isChecked"
              [disabled]="!canEdit()"
              (onChange)="toggleItem(item.key, item.isChecked)" />
            <span class="text-sm" [class.line-through]="item.isChecked" [class.text-500]="item.isChecked">{{
              item.label
            }}</span>
            @if (canEdit()) {
              <button
                pButton
                type="button"
                class="p-button-text p-button-danger p-button-sm ml-auto"
                pTooltip="Remove"
                tooltipPosition="left"
                (click)="removeItem(item.key)"><i class="pi pi-trash" pButtonIcon></i></button>
            }
          </div>
        }
        @if (canEdit()) {
          <div class="flex align-items-center gap-2 mt-2 pt-2 border-top-1 border-200">
            <input
              pInputText
              type="text"
              [(ngModel)]="newItemLabel"
              placeholder="New item..."
              class="p-inputtext-sm flex-1"
              (keydown.enter)="addItem()" />
            <button
              pButton
              type="button"
              class="p-button-text p-button-sm"
              [disabled]="!newItemLabel().trim()"
              (click)="addItem()"><i class="pi pi-plus" pButtonIcon></i></button>
          </div>
        }
      </div>
    }
  `,
})
export class BillingChecklistComponent implements OnInit {
  private billDataService = inject(BillDataService);
  private permissionService = inject(PermissionService);
  private store = inject(Store);

  billId = input.required<string>();
  readonly = input(false);

  checklist = signal<BillingChecklist | null>(null);
  newItemLabel = signal('');
  private currentUser = signal<User | undefined>(undefined);

  canEdit = computed(() => {
    if (this.readonly()) return false;
    const user = this.currentUser();
    if (!user) return false;
    return this.permissionService.hasEffectivePermission(user, 'Billing.Admin');
  });

  completedCount = computed(() => this.checklist()?.items.filter((i) => i.isChecked).length ?? 0);

  ngOnInit() {
    this.store.select(selectUser).subscribe((user) => this.currentUser.set(user));
    this.loadChecklist();
  }

  private loadChecklist() {
    this.billDataService.getChecklistForBill(this.billId()).subscribe((cl) => {
      this.checklist.set(cl);
    });
  }

  createChecklist() {
    this.billDataService.createChecklist(this.billId()).subscribe((cl) => {
      this.checklist.set(cl);
    });
  }

  toggleItem(key: string, isChecked: boolean) {
    const cl = this.checklist();
    if (!cl) return;
    this.billDataService.toggleChecklistItem(cl.id, key, isChecked).subscribe((updated) => {
      this.checklist.set(updated);
    });
  }

  addItem() {
    const cl = this.checklist();
    const label = this.newItemLabel().trim();
    if (!cl || !label) return;
    const key = label
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
    this.billDataService.addChecklistItem(cl.id, key, label).subscribe((updated) => {
      this.checklist.set(updated);
      this.newItemLabel.set('');
    });
  }

  removeItem(key: string) {
    const cl = this.checklist();
    if (!cl) return;
    this.billDataService.removeChecklistItem(cl.id, key).subscribe((updated) => {
      this.checklist.set(updated);
    });
  }
}
